import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { api } from "@/lib/api";
import { imgSrc } from "@/lib/img";
import { useAuth } from "@/context/AuthContext";
import { ReportButton } from "@/components/ReportButton";
import { RoleBadge } from "@/components/RoleBadge";
import { MessagesSquare, MapPinCheck, Timer, Send } from "lucide-react";
import { toast } from "sonner";

// Event group chat for accepted volunteers + host (extracted from
// OpportunityDetail in the Phase 15 component split — behaviour unchanged).
export function GroupChat({ oppId, eventDate }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const listRef = useRef();

  const isEventDay = eventDate && new Date(eventDate).toDateString() === new Date().toDateString();

  const load = () => api.groupMessages(oppId).then(setMessages).catch(() => {});
  useEffect(() => {
    load();
    const t = setInterval(load, 12000);
    return () => clearInterval(t);
  }, [oppId]); // eslint-disable-line react-hooks/exhaustive-deps -- render-scoped fetch helper; effect keys on its real inputs

  // Scroll only the message list, never the page (tab switch must not jump).
  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length]);

  const send = async (content, kind = "text") => {
    const body = (content ?? text).trim();
    if (!body) return;
    try {
      const msg = await api.sendGroupMessage(oppId, body, kind);
      setMessages((m) => [...m, msg]);
      if (kind === "text") setText("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send message");
    }
  };

  return (
    <div data-testid="group-chat-panel" className="bg-white border border-neutral-200 rounded-xl p-5">
      <div className="text-xs uppercase tracking-[0.18em] text-forest/70 font-bold flex items-center gap-1.5">
        <MessagesSquare className="w-4 h-4 text-terracotta" /> Event group chat
      </div>
      {isEventDay && (
        <div data-testid="quick-status-row" className="flex gap-2 mt-3">
          <button
            data-testid="quick-im-here"
            onClick={() => send("I'm here!", "status")}
            className="flex-1 text-xs font-medium px-3 py-2 rounded-full bg-forest/10 text-forest hover:bg-forest hover:text-white transition-colors inline-flex items-center justify-center gap-1.5"
          >
            <MapPinCheck className="w-3.5 h-3.5" /> I'm here
          </button>
          <button
            data-testid="quick-running-late"
            onClick={() => send("Running late — be there soon!", "status")}
            className="flex-1 text-xs font-medium px-3 py-2 rounded-full bg-amber-100 text-amber-800 hover:bg-amber-500 hover:text-white transition-colors inline-flex items-center justify-center gap-1.5"
          >
            <Timer className="w-3.5 h-3.5" /> Running late
          </button>
        </div>
      )}
      <div ref={listRef} className="mt-3 max-h-64 overflow-y-auto space-y-3 pr-1">
        {messages.length === 0 && <div className="text-sm text-neutral-500">No messages yet — say hello!</div>}
        {messages.map((m) => (
          m.kind === "status" ? (
            <div key={m.id} data-testid={`status-msg-${m.id}`} className="flex justify-center">
              <span className={`text-[11px] font-medium px-3 py-1 rounded-full inline-flex items-center gap-1.5 ${m.content.startsWith("I'm here") ? "bg-forest/10 text-forest" : "bg-amber-100 text-amber-800"}`}>
                {m.content.startsWith("I'm here") ? <MapPinCheck className="w-3 h-3" /> : <Timer className="w-3 h-3" />}
                {m.user_name}{m.is_host ? " (host)" : ""} — {m.content}
              </span>
            </div>
          ) : (
          <div key={m.id} className="flex items-start gap-2.5 group">
            <Avatar className="w-7 h-7 shrink-0">
              <AvatarImage src={imgSrc(m.picture)} alt={m.user_name} />
              <AvatarFallback className="bg-forest text-white text-[10px]">{m.user_name?.[0]}</AvatarFallback>
            </Avatar>
            <div>
              <div className="text-[11px] text-neutral-500">
                {m.user_name}{m.is_host && <span className="text-terracotta font-medium"> · Host</span>}
                {m.role_badge && <RoleBadge badge={m.role_badge} className="ml-1.5" />}
              </div>
              <div className="text-sm text-neutral-700 bg-neutral-50 border border-neutral-100 rounded-lg px-3 py-1.5 mt-0.5">{m.content}</div>
            </div>
            {user && m.user_id !== user.user_id && (
              <ReportButton targetType="group_message" targetId={m.id} label=""
                className="shrink-0 mt-1 opacity-0 group-hover:opacity-100" />
            )}
          </div>
          )
        ))}
      </div>
      <div className="flex gap-2 mt-3">
        <Input
          data-testid="group-chat-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Message everyone…"
          className="h-10"
        />
        <Button data-testid="group-chat-send" onClick={() => send()} className="rounded-full bg-forest hover:bg-forest-hover h-10 px-4">
          <Send className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}
